'use client';

import * as React from 'react';
import { styled } from '@linaria/react';
import { useSpring, animated } from 'react-spring';

import useSound from '@/hooks/use-sound';

const BORDER_WIDTH = 2;

interface Props extends React.HTMLAttributes<HTMLInputElement> {
  size?: number;
  checked: boolean;
  label?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const Checkbox = ({
  id,
  size = 18,
  checked,
  label,
  onChange,
  ...delegated
}: Props) => {
  const [active, setActive] = React.useState(false);
  const generatedId = React.useId();
  const actualId = id || `checkbox-${generatedId}`;

  const springConfig = {
    tension: 400,
    friction: 22,
    clamp: !checked,
  };

  // We want the outline to shrink by 4px, regardless of `size`.
  const shrinkRatio = size / (size + 4);
  // We want the filling to cancel out the shrinking of the outline.
  const fillRatio = 1 / shrinkRatio;

  const filledScale = checked ? (active ? fillRatio : 1) : 0;
  const filledSpring = useSpring({
    transform: `scale(${filledScale})`,
    config: springConfig,
  });

  const outlineScale = active ? shrinkRatio : 1;
  const outlineSpring = useSpring({
    transform: `scale(${outlineScale})`,
    config: springConfig,
  });

  const [playActive] = useSound('/sounds/pop-down.mp3', {
    volume: 0.25,
  });
  const [playOn] = useSound('/sounds/pop-up-on.mp3', {
    volume: 0.25,
  });
  const [playOff] = useSound('/sounds/pop-up-off.mp3', {
    volume: 0.25,
  });

  return (
    <Wrapper>
      <RealInput
        type="checkbox"
        id={actualId}
        onMouseDown={() => {
          setActive(true);
          playActive();
        }}
        onMouseUp={() => {
          setActive(false);

          if (checked) {
            playOff();
          } else {
            playOn();
          }
        }}
        onChange={onChange}
      />
      <VisibleContents>
        <VisibleBox
          style={{ width: size, height: size, ...outlineSpring }}
        >
          <Filled style={filledSpring} />
        </VisibleBox>

        {label && <Label htmlFor={actualId}>{label}</Label>}
      </VisibleContents>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
`;

const RealInput = styled.input`
  position: absolute;
  z-index: 2;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  appearance: none;
  cursor: pointer;
`;

const VisibleContents = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
`;

const VisibleBox = styled(animated.div)`
  position: relative;
  border: ${BORDER_WIDTH}px solid var(--color-gray-700);
  border-radius: 4px;
  margin-right: 8px;

  ${RealInput}:hover + ${VisibleContents} & {
    border-color: var(--color-gray-1000);
  }

  ${RealInput}:focus-visible + ${VisibleContents} & {
    outline: 2px auto var(--color-primary);
    outline-offset: 2px;
  }
`;

const Filled = styled(animated.div)`
  position: absolute;
  z-index: 1;
  top: 2px;
  left: 2px;
  right: 2px;
  bottom: 2px;
  background: var(--color-primary);
  border-radius: 2px;
`;

const Label = styled.label`
  font-size: 1rem;
  font-family: var(--font-family);
  color: var(--color-gray-700);
`;

export default Checkbox;
